import React from "react";
import { createPortal } from "react-dom";
import { resolvePublicImageUrl } from "../../../helper/helper";
import ImageCropModal from "../../../components/ImageCropModal/ImageCropModal";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import type { SubmitHandler } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { ProfileDatePicker } from "../../profileOnboarding/components/ProfileDatePicker";
import {
  BALL_TYPES,
  type BallType,
  DEFAULT_TOURNAMENT_RULES,
  getBallTypeLabel,
  type ICreateTournamentUI,
  MATCH_TYPES,
  MatchTypeLabel,
  schema,
  TOURNAMENT_CATEGORIES,
  type MatchType,
  type TournamentCategory,
  type TournamentRulesState,
} from "./types/createtournament";
import type { IGetAllTournaments } from "../types/home";
import { QUERY_KEY } from "../../../services/queryEndPoint";
import { updateTournamentRequest } from "./api/updateTournamentApi";
import TournamentTickerConfigSection from "./components/TournamentTickerConfigSection";
import WaiverSettingsSection from "./components/WaiverSettingsSection";
import type { TickerRowState } from "./tournamentsDetails/matches/tickerAutomationConstants";
import "./UpdateTournamentModal.scss";

function toDateInput(d: string): string {
  if (!d) return "";
  return d.includes("T") ? d.slice(0, 10) : d.slice(0, 10);
}

function mapBallTypeFromApi(s: string): BallType {
  const u = (s || "").toLowerCase();
  if (u.includes("tennis")) return "BallType.tennis";
  if (u.includes("other")) return "BallType.other";
  return "BallType.leather";
}

function normalizeMatchType(mt: string): MatchType {
  const u = (mt || "").trim().toUpperCase().replace(/\s+/g, "_");
  if (u === "TEST_MATCH") return "TEST";
  if ((MATCH_TYPES as readonly string[]).includes(u)) return u as MatchType;
  if (mt?.toUpperCase().includes("TEST")) return "TEST";
  return "T20";
}

function normalizeCategory(cat: string): TournamentCategory {
  const u = (cat || "").toUpperCase();
  if (TOURNAMENT_CATEGORIES.includes(u as TournamentCategory)) return u as TournamentCategory;
  return "OTHER";
}


function tournamentToFormDefaults(
  t: IGetAllTournaments,
  leagueId: string,
): ICreateTournamentUI {
  const ccRaw = (t.organizerPhoneNumberCountryCode || "91").replace(/\D/g, "") || "91";
  const digits = (t.organizerPhoneNumber || "").replace(/\D/g, "");
  const national = digits.length >= 10 ? digits.slice(-10) : digits;
  const fullPhone = national ? `${ccRaw}${national}` : "";

  return {
    leagueId: t.leagueId || leagueId,
    bannerUri: t.bannerUri,
    canEditTournament: t.canEditTournament,
    isRosterLocked: t.isRosterLocked,
    canEditScoreCard: t.canEditScoreCard,
    isImpactPlayerAllowed: t.isImpactPlayerAllowed,
    tournamentName: t.tournamentName,
    description: t.description ?? "",
    organizerName: t.organizerName,
    organizerEmailId: t.organizerEmailId ?? "",
    organizerPhoneNumber: fullPhone,
    organizerPhoneNumberCountryCode: ccRaw,
    startDate: toDateInput(t.startDate),
    endDate: toDateInput(t.endDate),
    matchType: normalizeMatchType(t.matchType),
    maxOversPerBowler: t.maxOversPerBowler,
    pointForWin: t.pointForWin,
    pointForTie: t.pointForTie,
    pointForAbandoned: t.pointForAbandoned,
    pointForFirstInningsLead: t.pointForFirstInningsLead,
    pointForFirstInningsLoss: t.pointForFirstInningsLoss,
    pointForOutrightWin: t.pointForOutrightWin,
    bonusPointForWinByInnings: t.bonusPointForWinByInnings,
    ballType: mapBallTypeFromApi(t.ballType),
    tournamentCategory: normalizeCategory(t.tournamentCategory),
    maxOvers: t.maxOvers > 0 ? t.maxOvers : undefined,
    maxWickets: t.maxWickets > 0 ? t.maxWickets : undefined,
    bonusPointsEnabled: (t as unknown as { bonusPointsEnabled?: boolean }).bonusPointsEnabled ?? false,
    isStrictModeEnabled: t.strictModeEnabled ?? false,
    isTickerAutomationEnabled: t.isTickerAutomationEnabled ?? t.tickerAutomationEnabled ?? true,
    waiverRequired: t.waiverRequired ?? false,
    waiverId: t.waiverId ?? null,
  };
}

const MATCH_DAY_OPTIONS = [
  { value: "", label: "Select Match Days" },
  { value: "0", label: "Not specified" },
  { value: "1", label: "1 day" },
  { value: "2", label: "2 days" },
  { value: "3", label: "3 days" },
  { value: "4", label: "4 days" },
  { value: "5", label: "5 days" },
  { value: "6", label: "6 days" },
  { value: "7", label: "7 days" },
];

type Props = {
  open: boolean;
  tournament: IGetAllTournaments | null;
  leagueId: string | null;
  onOpenChange: (open: boolean) => void;
};

const UpdateTournamentModal: React.FC<Props> = ({ open, tournament, leagueId, onOpenChange }) => {
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [scheduledDays, setScheduledDays] = useState("0");
  const [tickerAutomationEnabled, setTickerAutomationEnabled] = useState(true);
  const [tickerRows, setTickerRows] = useState<TickerRowState[]>([]);
  const [waiverRequired, setWaiverRequired] = useState(false);
  const [waiverId, setWaiverId] = useState<string | null>(null);
  const [waiverError, setWaiverError] = useState<string | undefined>(undefined);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState<string | null>(null);
  const [tournamentRules, setTournamentRules] = useState<TournamentRulesState>(DEFAULT_TOURNAMENT_RULES);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState('');
  const [cropMimeType, setCropMimeType] = useState('image/jpeg');
  const [cropFileName, setCropFileName] = useState('banner');

  const effectiveLeagueId = leagueId ?? tournament?.leagueId ?? null;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setBannerFile(null);
        setBannerPreviewUrl(null);
        onOpenChange(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !tournament) return;
    const sd = tournament.scheduledDays;
    if (sd != null && sd >= 0) setScheduledDays(String(sd));
    else setScheduledDays("0");
    const tickerEnabled = tournament.isTickerAutomationEnabled ?? tournament.tickerAutomationEnabled;
    if (typeof tickerEnabled === "boolean") {
      setTickerAutomationEnabled(tickerEnabled);
    }
    setWaiverRequired(!!tournament.waiverRequired);
    setWaiverId(tournament.waiverId ?? null);
    setWaiverError(undefined);
    if (tournament.tournamentRule) {
      setTournamentRules({
        countWideBall: tournament.tournamentRule.countWideBall,
        countNoBall: tournament.tournamentRule.countNoBall,
        runsForWideBall: tournament.tournamentRule.runsForWideBall,
        runsForNoBall: tournament.tournamentRule.runsForNoBall,
        excludeRules: tournament.tournamentRule.excludeRules ?? [],
        excludeOvers: tournament.tournamentRule.excludeOvers ?? [],
      });
    } else {
      setTournamentRules(DEFAULT_TOURNAMENT_RULES);
    }
    setBannerFile(null);
    setBannerPreviewUrl(null);
    setCropOpen(false);
    setCropSrc('');
  }, [
    open,
    tournament?.tournamentId,
    tournament?.scheduledDays,
    tournament?.isTickerAutomationEnabled,
    tournament?.tickerAutomationEnabled,
  ]);

  const formDefaults = useMemo((): ICreateTournamentUI => {
    if (!tournament || !effectiveLeagueId) {
      return {
        leagueId: null,
        bannerUri: null,
        canEditTournament: null,
        isRosterLocked: false,
        canEditScoreCard: false,
        isImpactPlayerAllowed: false,
        tournamentName: "",
        description: "",
        organizerName: "",
        organizerPhoneNumber: "",
        organizerPhoneNumberCountryCode: "",
        organizerEmailId: "",
        startDate: "",
        endDate: "",
        matchType: "T20",
        ballType: "BallType.leather",
        tournamentCategory: "OTHER",
        maxOversPerBowler: 1,
        pointForWin: 0,
        pointForTie: 0,
        pointForAbandoned: 0,
        bonusPointsEnabled: false,
        isStrictModeEnabled: false,
        isTickerAutomationEnabled: true,
        waiverRequired: false,
        waiverId: null,
      };
    }
    return tournamentToFormDefaults(tournament, effectiveLeagueId);
  }, [tournament, effectiveLeagueId]);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ICreateTournamentUI, unknown, ICreateTournamentUI>({
    resolver: yupResolver(schema),
    mode: "onSubmit",
    defaultValues: formDefaults,
  });

  useEffect(() => {
    if (open && tournament) {
      reset(formDefaults);
    }
  }, [open, tournament?.tournamentId]);

  const matchTypeWatch = watch("matchType");
  const ballTypeWatch = watch("ballType");
  const categoryWatch = watch("tournamentCategory");
  const maxOversWatch = watch("maxOvers");
  const overCount = (() => {
    switch (matchTypeWatch) {
      case "T10": return 10;
      case "T20": return 20;
      case "ODI": return 50;
      case "THE_HUNDRED": return 20;
      case "INDOOR": return 10;
      case "OTHER_LIMITED_OVER":
        return typeof maxOversWatch === "number" && maxOversWatch > 0 ? maxOversWatch : 20;
      default: return 20;
    }
  })();

  useEffect(() => {
    setTournamentRules((r) => ({ ...r, excludeOvers: [] }));
  }, [matchTypeWatch]);

  const fc = (hasErr?: boolean) => `form-control ${hasErr ? "field-error" : ""}`;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setBannerFile(null);
      setBannerPreviewUrl(null);
    }
    onOpenChange(next);
  };

  const onInvalid = (formErrors: Record<string, unknown>) => {
    console.error("Update tournament validation failed:", formErrors);
    const fieldNames = Object.keys(formErrors);
    toast.error(
      fieldNames.length
        ? `Please fix: ${fieldNames.join(", ")}`
        : "Please fix the highlighted fields before saving.",
    );
    setTimeout(() => {
      const firstErrorField = document.querySelector<HTMLElement>(
        ".update-tournament-modal-form .field-error, .update-tournament-modal-form .err-msg",
      );
      firstErrorField?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 0);
  };

  const onSave: SubmitHandler<ICreateTournamentUI> = async (data) => {
    if (!tournament?.tournamentId || !effectiveLeagueId) return;
    if (waiverRequired && !waiverId) {
      setWaiverError("Please select a waiver form");
      return;
    }
    setWaiverError(undefined);
    setSubmitting(true);
    try {
      const draft: Record<string, unknown> = {
        ...data,
        leagueId: effectiveLeagueId,
        scheduledDays: Number(scheduledDays) || 0,
        isTickerAutomationEnabled: tickerAutomationEnabled,
        isAbandonedGameCounted: tournament.isAbandonedGameCounted ?? true,
        rankingPreference: tournament.rankingPreference ?? "TOTAL_POINTS",
        isPowerPlayEnabled: tournament.isPowerPlayEnabled ?? false,
        maxPowerPlayOvers: tournament.maxPowerPlayOvers ?? 0,
        waiverRequired,
        waiverId,
        tournamentRules,
      };
      await updateTournamentRequest({
        tournamentId: tournament.tournamentId,
        leagueId: effectiveLeagueId,
        draft,
        bannerFile,
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getTournamentsByLeagues, effectiveLeagueId],
      });
      toast.success("Tournament updated successfully.");
      handleOpenChange(false);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: unknown } };
      const d = ax?.response?.data;
      const message =
        typeof d === "string"
          ? d
          : d && typeof d === "object" && "message" in d
            ? String((d as { message: unknown }).message)
            : null;
      toast.error(message || "Failed to update tournament. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBannerCropConfirm = (croppedFile: File, previewUrl: string) => {
    setCropOpen(false);
    if (bannerPreviewUrl) URL.revokeObjectURL(bannerPreviewUrl);
    setBannerFile(croppedFile);
    setBannerPreviewUrl(previewUrl);
  };

  if (!open || !tournament) {
    return null;
  }

  return createPortal(
    <>
    <ImageCropModal
      open={cropOpen}
      imageSrc={cropSrc}
      aspect={16 / 9}
      mimeType={cropMimeType}
      fileName={cropFileName}
      onCrop={handleBannerCropConfirm}
      onCancel={() => setCropOpen(false)}
    />
    <div
      className="update-tournament-modal-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby="update-tournament-modal-title"
    >
      <button
        type="button"
        className="update-tournament-modal-overlay"
        aria-label="Close dialog"
        onClick={() => handleOpenChange(false)}
      />
      <div
        className="update-tournament-modal-content"
        role="document"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="update-tournament-modal-header">
          <h2 id="update-tournament-modal-title" className="update-tournament-modal-title">
            Update Tournament
          </h2>
          <button type="button" className="update-tournament-modal-close" aria-label="Close" onClick={() => handleOpenChange(false)}>
            ×
          </button>
        </div>

        {effectiveLeagueId ? (
            <form className="update-tournament-modal-form" onSubmit={handleSubmit(onSave, onInvalid)}>
              <div className="form-control-wrap">
                <div className="form-group">
                  <label>
                    Tournament Name <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter Tournament Name"
                    className={fc(!!errors.tournamentName)}
                    {...register("tournamentName")}
                  />
                  {errors.tournamentName && <p className="err-msg">{errors.tournamentName.message}</p>}
                </div>
                <div className="form-group">
                  <label>
                    Mobile Number <span className="req">*</span>
                  </label>
                  <div className="phone">
                    <Controller
                      control={control}
                      name="organizerPhoneNumber"
                      render={({ field }) => (
                        <PhoneInput
                          country="in"
                          value={field.value ?? ""}
                          countryCodeEditable={false}
                          onChange={(value, country: { dialCode?: string }) => {
                            field.onChange(value);
                            setValue("organizerPhoneNumberCountryCode", country?.dialCode || "", {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }}
                          inputClass={`${fc(!!errors.organizerPhoneNumber)} phone-input-field`}
                          containerClass="phone-input-container"
                          placeholder="Enter Mobile Number"
                          enableSearch
                        />
                      )}
                    />
                    <input type="hidden" {...register("organizerPhoneNumberCountryCode")} />
                  </div>
                  {(errors.organizerPhoneNumber || errors.organizerPhoneNumberCountryCode) && (
                    <p className="err-msg">
                      {errors.organizerPhoneNumber?.message ||
                        errors.organizerPhoneNumberCountryCode?.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="update-tournament-dates-block">
                <label className="update-tournament-dates-label" htmlFor="upd-tournament-start">
                  Tournament Dates <span className="req">*</span>
                </label>
                <div className="date-range-pair">
                  <div className="date-field">
                    <Controller
                      control={control}
                      name="startDate"
                      render={({ field }) => (
                        <ProfileDatePicker
                          id="upd-tournament-start"
                          className="update-tournament-date-picker"
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          placeholder="Start Date"
                          hasError={!!errors.startDate}
                        />
                      )}
                    />
                    {errors.startDate && <p className="err-msg">{errors.startDate.message}</p>}
                  </div>
                  <div className="date-field">
                    <Controller
                      control={control}
                      name="endDate"
                      render={({ field }) => (
                        <ProfileDatePicker
                          id="upd-tournament-end"
                          className="update-tournament-date-picker"
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          placeholder="End Date"
                          hasError={!!errors.endDate}
                        />
                      )}
                    />
                    {errors.endDate && <p className="err-msg">{errors.endDate.message}</p>}
                  </div>
                </div>
              </div>

              <div className="form-control-wrap">
                <div className="form-group">
                  <label>
                    Organiser Email <span className="req">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="Enter Email Id"
                    className={fc(!!errors.organizerEmailId)}
                    {...register("organizerEmailId")}
                  />
                  {errors.organizerEmailId && <p className="err-msg">{errors.organizerEmailId.message}</p>}
                </div>
                <div className="form-group">
                  <label>
                    Organiser name <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter Organiser Name"
                    className={fc(!!errors.organizerName)}
                    {...register("organizerName")}
                  />
                  {errors.organizerName && <p className="err-msg">{errors.organizerName.message}</p>}
                </div>
              </div>

              <div className="score-rule-wrap update-tournament-score-rule">
                {matchTypeWatch !== "TEST" && (
                  <div className="over-bolwer">
                    <label>
                      Overs per Bowler <span className="req">*</span>
                    </label>
                    <input
                      type="number"
                      placeholder="Enter overs"
                      className={fc(!!errors.maxOversPerBowler)}
                      {...register("maxOversPerBowler", { valueAsNumber: true })}
                    />
                    {errors.maxOversPerBowler && (
                      <p className="err-msg">{errors.maxOversPerBowler.message}</p>
                    )}
                  </div>
                )}
                <div className="points-data-wrap">
                  {matchTypeWatch !== "TEST" && (
                    <div className="points-data">
                      <label>Points for Win</label>
                      <input
                        type="number"
                        placeholder="Enter Points"
                        className={fc(!!errors.pointForWin)}
                        {...register("pointForWin", { valueAsNumber: true })}
                      />
                      {errors.pointForWin && <p className="err-msg">{errors.pointForWin.message}</p>}
                    </div>
                  )}
                  <div className="points-data">
                    <label>Points for Tie</label>
                    <input
                      type="number"
                      placeholder="Enter Points"
                      className={fc(!!errors.pointForTie)}
                      {...register("pointForTie", { valueAsNumber: true })}
                    />
                    {errors.pointForTie && <p className="err-msg">{errors.pointForTie.message}</p>}
                  </div>
                  <div className="points-data">
                    <label>Points for Abandoned</label>
                    <input
                      type="number"
                      placeholder="Enter Points"
                      className={fc(!!errors.pointForAbandoned)}
                      {...register("pointForAbandoned", { valueAsNumber: true })}
                    />
                    {errors.pointForAbandoned && (
                      <p className="err-msg">{errors.pointForAbandoned.message}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="match-type-wrapper">
                <div className="match-type">
                  <label>Match Type</label>
                  <div className="chip-group">
                    {MATCH_TYPES.map((mt) => (
                      <button
                        type="button"
                        key={mt}
                        className={`chip ${matchTypeWatch === mt ? "selected" : ""}`}
                        onClick={() =>
                          setValue("matchType", mt, { shouldValidate: true, shouldDirty: true })
                        }
                      >
                        {MatchTypeLabel[mt]}
                      </button>
                    ))}
                  </div>
                  {errors.matchType && <p className="err-msg">{errors.matchType.message as string}</p>}
                </div>
                <div className="ball-type">
                  <label>Ball Type</label>
                  <div className="chip-group">
                    {BALL_TYPES.map((bt) => (
                      <button
                        type="button"
                        key={bt}
                        className={`chip ${ballTypeWatch === bt ? "selected" : ""}`}
                        onClick={() =>
                          setValue("ballType", bt, { shouldValidate: true, shouldDirty: true })
                        }
                      >
                        {getBallTypeLabel(bt as BallType)}
                      </button>
                    ))}
                  </div>
                  {errors.ballType && <p className="err-msg">{errors.ballType.message as string}</p>}
                </div>
              </div>

              {matchTypeWatch === "OTHER_LIMITED_OVER" && (
                <div className="other-limited-wrap">
                  <div className="over-wrap">
                    <label>
                      No. Of Overs <span className="req">*</span>
                    </label>
                    <input
                      className={fc(!!errors.maxOvers)}
                      placeholder="Enter Overs"
                      {...register("maxOvers", { valueAsNumber: true })}
                    />
                    {errors.maxOvers && <p className="err-msg">{errors.maxOvers.message as string}</p>}
                  </div>
                  <div className="wicket-wrap">
                    <label>
                      No. Of Wickets <span className="req">*</span>
                    </label>
                    <input
                      className={fc(!!errors.maxWickets)}
                      placeholder="Enter wickets"
                      {...register("maxWickets", { valueAsNumber: true })}
                    />
                    {errors.maxWickets && (
                      <p className="err-msg">{errors.maxWickets.message as string}</p>
                    )}
                  </div>
                </div>
              )}

              {matchTypeWatch === "TEST" && (
                <div style={{ marginTop: "16px" }}>
                  <div className="form-control-wrap">
                    <div className="form-group">
                      <label>Points for First Innings Lead</label>
                      <input
                        type="number"
                        placeholder="Enter Points"
                        className={fc(!!errors.pointForFirstInningsLead)}
                        {...register("pointForFirstInningsLead", { valueAsNumber: true })}
                      />
                      {errors.pointForFirstInningsLead && (
                        <p className="err-msg">{errors.pointForFirstInningsLead.message}</p>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Points for First Innings Loss</label>
                      <input
                        type="number"
                        placeholder="Enter Points"
                        className={fc(!!errors.pointForFirstInningsLoss)}
                        {...register("pointForFirstInningsLoss", { valueAsNumber: true })}
                      />
                      {errors.pointForFirstInningsLoss && (
                        <p className="err-msg">{errors.pointForFirstInningsLoss.message}</p>
                      )}
                    </div>
                  </div>
                  <div className="form-control-wrap">
                    <div className="form-group">
                      <label>Points for Outright Win</label>
                      <input
                        type="number"
                        placeholder="Enter Points"
                        className={fc(!!errors.pointForOutrightWin)}
                        {...register("pointForOutrightWin", { valueAsNumber: true })}
                      />
                      {errors.pointForOutrightWin && (
                        <p className="err-msg">{errors.pointForOutrightWin.message}</p>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Bonus Points for Win by Innings</label>
                      <input
                        type="number"
                        placeholder="Enter Points"
                        className={fc(!!errors.bonusPointForWinByInnings)}
                        {...register("bonusPointForWinByInnings", { valueAsNumber: true })}
                      />
                      {errors.bonusPointForWinByInnings && (
                        <p className="err-msg">{errors.bonusPointForWinByInnings.message}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Wide/No Ball Rules — shown for all match types */}
              <div style={{ marginTop: "16px" }}>
                <hr className="soft-sep" />
                <h2 className="sec-title">Wide/No Ball Rules</h2>

                <div className="tournament-rules-card">
                  <div className="tournament-rule-row">
                    <span className="rule-badge">A</span>
                    <span className="rule-label">Count wide as a legal delivery</span>
                    <label className="toggle" style={{ margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={tournamentRules.countWideBall}
                        onChange={(e) => setTournamentRules((r) => ({ ...r, countWideBall: e.target.checked }))}
                      />
                      <span className="slider" />
                    </label>
                  </div>
                  <div className="tournament-rule-row">
                    <span className="rule-badge">B</span>
                    <span className="rule-label">Wide</span>
                    <div className="rule-counter">
                      <button type="button" className="counter-btn"
                        onClick={() => setTournamentRules((r) => ({ ...r, runsForWideBall: Math.max(0, r.runsForWideBall - 1) }))}>−</button>
                      <span className="counter-value">{tournamentRules.runsForWideBall}</span>
                      <button type="button" className="counter-btn"
                        onClick={() => setTournamentRules((r) => ({ ...r, runsForWideBall: r.runsForWideBall + 1 }))}>+</button>
                    </div>
                  </div>
                  <div className="tournament-rule-row">
                    <span className="rule-badge">C</span>
                    <span className="rule-label">Count no ball as a legal delivery</span>
                    <label className="toggle" style={{ margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={tournamentRules.countNoBall}
                        onChange={(e) => setTournamentRules((r) => ({ ...r, countNoBall: e.target.checked }))}
                      />
                      <span className="slider" />
                    </label>
                  </div>
                  <div className="tournament-rule-row">
                    <span className="rule-badge">D</span>
                    <span className="rule-label">No Ball</span>
                    <div className="rule-counter">
                      <button type="button" className="counter-btn"
                        onClick={() => setTournamentRules((r) => ({ ...r, runsForNoBall: Math.max(0, r.runsForNoBall - 1) }))}>−</button>
                      <span className="counter-value">{tournamentRules.runsForNoBall}</span>
                      <button type="button" className="counter-btn"
                        onClick={() => setTournamentRules((r) => ({ ...r, runsForNoBall: r.runsForNoBall + 1 }))}>+</button>
                    </div>
                  </div>
                </div>

                {matchTypeWatch !== "TEST" && (
                  <div className="tournament-rules-meta">
                    <div className="rules-meta-row">
                      <span className="rules-meta-label">Ignore rules</span>
                      <div className="rules-chips">
                        {['A', 'B', 'C', 'D'].map((rule) => (
                          <button
                            type="button"
                            key={rule}
                            className={`rules-chip ${tournamentRules.excludeRules.includes(rule) ? 'selected' : ''}`}
                            onClick={() =>
                              setTournamentRules((r) => ({
                                ...r,
                                excludeRules: r.excludeRules.includes(rule)
                                  ? r.excludeRules.filter((x) => x !== rule)
                                  : [...r.excludeRules, rule],
                              }))
                            }
                          >{rule}</button>
                        ))}
                      </div>
                    </div>
                    <div className="rules-meta-row">
                      <span className="rules-meta-label">For overs</span>
                      <div className="rules-chips overs-chips">
                        {Array.from({ length: overCount }, (_, i) => i + 1).map((over) => (
                          <button
                            type="button"
                            key={over}
                            className={`rules-chip ${tournamentRules.excludeOvers.includes(over) ? 'selected' : ''}`}
                            onClick={() =>
                              setTournamentRules((r) => ({
                                ...r,
                                excludeOvers: r.excludeOvers.includes(over)
                                  ? r.excludeOvers.filter((x) => x !== over)
                                  : [...r.excludeOvers, over],
                              }))
                            }
                          >{over}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="tournament-category-wrapper">
                <div className="category-wrap">
                  <label>Tournament Category</label>
                  <div className="chip-group">
                    {TOURNAMENT_CATEGORIES.map((cat) => (
                      <button
                        type="button"
                        key={cat}
                        className={`chip ${categoryWatch === cat ? "selected" : ""}`}
                        onClick={() =>
                          setValue("tournamentCategory", cat, {
                            shouldValidate: true,
                            shouldDirty: true,
                          })
                        }
                      >
                        {cat.charAt(0) + cat.slice(1).toLowerCase()}
                      </button>
                    ))}
                  </div>
                  {errors.tournamentCategory && (
                    <p className="err-msg">{errors.tournamentCategory.message as string}</p>
                  )}
                </div>
                <div className="form-group update-tournament-match-days">
                  <label>Match Days</label>
                  <select
                    className="form-control"
                    value={scheduledDays}
                    onChange={(e) => setScheduledDays(e.target.value)}
                  >
                    {MATCH_DAY_OPTIONS.map((o) => (
                      <option key={o.value || "empty"} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="toggles update-tournament-toggles">
                <label className="toggle">
                  <span className="label">Lock Roster</span>
                  <input type="checkbox" {...register("isRosterLocked")} />
                  <span className="slider" />
                </label>
                <label className="toggle">
                  <span className="label">Allow Other Players to Edit Score</span>
                  <input type="checkbox" {...register("canEditScoreCard")} />
                  <span className="slider" />
                </label>
                <label className="toggle">
                  <span className="label">Allow Impact Player</span>
                  <input type="checkbox" {...register("isImpactPlayerAllowed")} />
                  <span className="slider" />
                </label>
                <label className="toggle">
                  <span className="label">Enable Bonus Points</span>
                  <input type="checkbox" {...register("bonusPointsEnabled")} />
                  <span className="slider" />
                </label>
                <label className="toggle">
                  <span className="label">Strict Mode</span>
                  <input type="checkbox" {...register("isStrictModeEnabled")} />
                  <span className="slider" />
                </label>
                <label className="toggle">
                  <span className="label">Enable Ticker Automation</span>
                  <input
                    type="checkbox"
                    checked={tickerAutomationEnabled}
                    onChange={() => setTickerAutomationEnabled((v) => !v)}
                  />
                  <span className="slider" />
                </label>
              </div>

              {tickerAutomationEnabled && (
                <TournamentTickerConfigSection
                  initial={tickerRows}
                  onChange={setTickerRows}
                />
              )}

              <div className="form-group-textarea update-tournament-description">
                <label>
                  Tournament Description <span className="req">*</span>
                </label>
                <textarea
                  rows={5}
                  placeholder="Describe your tournament"
                  className={`textarea form-control ${errors.description ? "field-error" : ""}`}
                  {...register("description")}
                />
                {errors.description && <p className="err-msg">{errors.description.message}</p>}
              </div>

              <div className="form-group update-tournament-banner-row">
                <label>Banner image (optional)</label>
                {bannerPreviewUrl ? (
                  <div className="banner-preview-wrap">
                    <img src={bannerPreviewUrl} alt="New banner preview" className="banner-preview-img" />
                    <span className="banner-preview-caption">New banner (preview)</span>
                  </div>
                ) : tournament.bannerUri ? (
                  <div className="banner-preview-wrap">
                    <img src={resolvePublicImageUrl(tournament.bannerUri)} alt="Current banner" className="banner-preview-img" />
                    <span className="banner-preview-caption">Current banner</span>
                  </div>
                ) : null}
                <input
                  type="file"
                  accept="image/*"
                  className="form-control"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      setCropSrc(reader.result as string);
                      setCropMimeType(file.type || 'image/jpeg');
                      setCropFileName(file.name);
                      setCropOpen(true);
                    };
                    reader.readAsDataURL(file);
                    e.target.value = '';
                  }}
                />
              </div>

              <WaiverSettingsSection
                leagueId={effectiveLeagueId}
                waiverRequired={waiverRequired}
                waiverId={waiverId}
                onWaiverRequiredChange={(value) => {
                  setWaiverRequired(value);
                  setValue("waiverRequired", value, { shouldValidate: true, shouldDirty: true });
                  setWaiverError(undefined);
                }}
                onWaiverIdChange={(value) => {
                  setWaiverId(value);
                  setValue("waiverId", value, { shouldValidate: true, shouldDirty: true });
                  setWaiverError(undefined);
                }}
                error={waiverError || (errors.waiverId?.message as string | undefined)}
              />

              <div className="actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? "Updating…" : "Update Tournament"}
                </button>
              </div>
            </form>
        ) : (
          <p className="update-tournament-modal-missing-league">League context is missing. Close and try again.</p>
        )}
      </div>
    </div>
    </>,
    document.body,
  );
};

export default UpdateTournamentModal;
